/**
 * The region hierarchy, in the flat shape the scope picker takes (BON-538).
 *
 * An adapter, not a fetch of its own: the picker is deliberately ignorant of
 * where regions come from, so this is the caller's side of that contract.
 * Shared by every page that scopes itself by region, so the pickers on Venues,
 * Sales, Teachers, Supplies and Corp Events all list the same tree.
 *
 * Colour lives in `venues/roster/region-colors.ts`, which needs the sibling
 * ORDER the nested tree carries and the flat row does not.
 */
import { useQuery } from '@tanstack/react-query';

import { fetchRegions, type RegionNode } from '../../../../api/regions';

import type { ScopeRegion } from './scope.types';

/** Depth-first, parents before children. */
function flatten(nodes: readonly RegionNode[], depth: number, out: ScopeRegion[]): void {
  for (const node of nodes) {
    out.push({
      id: node.id,
      name: node.name,
      // A region that predates the slug backfill still has to be addressable.
      slug: node.slug ?? node.id,
      parentId: node.parentId,
      depth,
      directorName: node.directorName ?? null,
    });
    flatten(node.children, depth + 1, out);
  }
}

export function flattenRegions(tree: readonly RegionNode[]): ScopeRegion[] {
  const out: ScopeRegion[] = [];
  flatten(tree, 0, out);
  return out;
}

/** The region hierarchy behind the scope picker. */
export function useRegionHierarchy() {
  return useQuery({
    queryKey: ['scope.regions'],
    queryFn: fetchRegions,
    // The hierarchy changes when someone edits it in Region Management, which
    // is rare enough that a five-minute window is generous.
    staleTime: 5 * 60_000,
  });
}
